import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Download, Trash2, Pencil, Eye, EyeOff, X, FileText, Image as ImageIcon, File as FileIcon, Upload, MoreVertical, AlertCircle, CheckCircle2 } from "lucide-react";
import DocumentCategoriesModal from "./DocumentCategoriesModal";
import DocumentPreviewModal from "./DocumentPreviewModal";
import UploadConfirmationModal from "./UploadConfirmationModal";
import UploadBlockedModal from "./UploadBlockedModal";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";
import { useAuth } from "../../contexts/AuthContext";

const TYPES = ["Contratto", "Logo", "Altro"];
const TYPE_LABEL = { Contratto: "Contratto", Logo: "Logo", Altro: "Altro" };
const VIS_LABELS = [
    { key: "all", label: "Tutti" },
    { key: "true", label: "Visibili al cliente" },
    { key: "false", label: "Solo interni" },
];

// Whitelist upload (Task 3): PDF + Word + Excel + immagini
// Fonte di verità: backend `ALLOWED_MIME` in routes/internal_documenti.py
const ACCEPT_ATTR = [
    ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".png", ".jpg", ".jpeg", ".webp", ".zip",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/png", "image/jpeg", "image/webp",
    "application/zip", "application/x-zip-compressed",
].join(",");
const ACCEPT_HINT = "Formati accettati: PDF, Word (.doc/.docx), Excel (.xls/.xlsx), PNG, JPG, WebP, ZIP · Max 20 MB";

function fmtSize(b) {
    if (b == null) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function typeIcon(t) {
    if (t === "Contratto") return <FileText size={14} strokeWidth={2} />;
    if (t === "Logo") return <ImageIcon size={14} strokeWidth={2} />;
    return <FileIcon size={14} strokeWidth={2} />;
}

function TypeChip({ type }) {
    const cls = type === "Contratto" ? "lt-chip-doc--contratto"
        : type === "Logo" ? "lt-chip-doc--logo"
        : "lt-chip-doc--altro";
    return (
        <span className={`lt-chip-doc ${cls}`} data-testid={`doc-type-${type}`}>
            {typeIcon(type)} {TYPE_LABEL[type] || type}
        </span>
    );
}

function VisibilityBadge({ visible }) {
    if (visible) return (
        <span className="lt-visibility lt-visibility--public" data-testid="doc-vis-public">
            <Eye size={12} strokeWidth={2} /> Visibile al cliente
        </span>
    );
    return (
        <span className="lt-visibility lt-visibility--internal" data-testid="doc-vis-internal">
            <EyeOff size={12} strokeWidth={2} /> Interno
        </span>
    );
}

async function streamDownload(id, fallbackName) {
    try {
        const res = await fetch(`${API}/internal/documenti/${id}/download`, {
            credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // Extract filename from Content-Disposition if available
        const cd = res.headers.get("Content-Disposition") || "";
        let filename = fallbackName || "documento";
        const m1 = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const m2 = cd.match(/filename="([^"]+)"/i);
        if (m1) filename = decodeURIComponent(m1[1]);
        else if (m2) filename = m2[1];
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Defer revoke so click can complete
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
        toast.error("Download fallito");
    }
}

function UploadForm({ clients, categories, onClose, onUploaded, onBlocked }) {
    const [clientId, setClientId] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState("Contratto");
    const [visible, setVisible] = useState(true);
    const [notes, setNotes] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const inputRef = useRef(null);

    const onPick = () => inputRef.current?.click();
    const onFile = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;
        const tooBig = picked.filter((f) => f.size > 20 * 1024 * 1024);
        if (tooBig.length > 0) {
            toast.error(`${tooBig.length === 1 ? "File" : "File"} troppo ${tooBig.length === 1 ? "grande" : "grandi"} (max 20 MB): ${tooBig.map((f) => f.name).join(", ")}`);
            return;
        }
        setFiles(picked);
        // Auto-set name se singolo file e nome vuoto
        if (picked.length === 1 && !name) {
            setName(picked[0].name.replace(/\.[^.]+$/, ""));
        }
        // Reset input value per permettere re-selezione dello stesso file
        e.target.value = "";
    };

    const validate = () => {
        if (!clientId || files.length === 0 || !type) {
            toast.error("Compila tutti i campi obbligatori");
            return false;
        }
        // Il campo "Nome" è richiesto SOLO se single-file (in multi-file
        // ogni file usa il proprio nome originale).
        if (files.length === 1 && !name.trim()) {
            toast.error("Compila il nome documento");
            return false;
        }
        return true;
    };

    const openConfirm = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (saving) return;
        if (!validate()) return;
        setShowConfirm(true);
    };

    const doUploadAll = async () => {
        setSaving(true);
        const selectedClient = clients.find((c) => c.id === clientId);
        let successCount = 0;
        let failedCount = 0;
        let blockedCount = 0;
        let needsReviewCount = 0;
        const warningMessages = [];
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("client_id", clientId);
                // Multi-file: usa il basename del file come "name"; single-file: usa il campo "name"
                const docName = files.length === 1
                    ? name.trim()
                    : file.name.replace(/\.[^.]+$/, "").slice(0, 200);
                fd.append("name", docName);
                fd.append("type", type);
                fd.append("visible_to_client", String(visible));
                if (notes.trim()) fd.append("notes", notes.trim());
                if (categoryId) fd.append("category_id", categoryId);
                fd.append("file", file);
                try {
                    const { data } = await api.post("/internal/documenti", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    successCount += 1;
                    if (data?.needs_review) needsReviewCount += 1;
                    // Mitigazione A: ZIP ritorna warning_message informativo
                    if (data?.warning_message) {
                        warningMessages.push(data.warning_message);
                    }
                } catch (err) {
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    // Phase 9.6: 422 con detail strutturato = content-check block
                    if (status === 422 && detail && typeof detail === "object" && detail.blocked_client) {
                        blockedCount += 1;
                        if (typeof onBlocked === "function") {
                            onBlocked({
                                detail,
                                filename: file.name,
                                file,
                                uploadContext: {
                                    clientId, name: docName, type,
                                    visible, notes: notes.trim(),
                                    categoryId,
                                },
                            });
                        }
                    } else {
                        failedCount += 1;
                        const msg = typeof detail === "string" ? detail : (detail?.message || "errore");
                        toast.error(`Errore su «${file.name}»: ${msg}`);
                    }
                }
            }
            const okOnly = successCount > 0 && failedCount === 0 && blockedCount === 0;
            if (okOnly) {
                if (needsReviewCount > 0) {
                    toast.warning(`${successCount} documenti caricati, ${needsReviewCount} richiedono verifica manuale`);
                } else {
                    toast.success(`${successCount === 1 ? "Documento caricato" : `${successCount} documenti caricati`} per ${selectedClient?.company_name || "cliente"}`);
                }
                onUploaded();
            } else if (successCount > 0) {
                const parts = [`${successCount} caricati`];
                if (blockedCount > 0) parts.push(`${blockedCount} bloccati per contenuto`);
                if (failedCount > 0) parts.push(`${failedCount} falliti`);
                if (needsReviewCount > 0) parts.push(`${needsReviewCount} da verificare`);
                toast.warning(parts.join(", "));
                onUploaded();
            }
            // Mitigazione A ZIP: surface warning_message dedicato (toast info)
            const uniqWarnings = Array.from(new Set(warningMessages));
            uniqWarnings.forEach((msg) => {
                toast.info(msg, { duration: 5000 });
            });
            // Se tutti bloccati/falliti: gli errori sono già mostrati (blocked modal + toast) — non chiudere l'upload modal
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const activeCategories = (categories || []).filter((c) => c.active !== false);
    const selectedClient = clients.find((c) => c.id === clientId);

    return (
        <>
            <form onSubmit={openConfirm} method="post" action="#" noValidate className="lt-scad-form" data-testid="doc-upload-form">
                <div className="lt-form-row">
                    <label className="lt-label">Cliente *</label>
                    <select className="lt-select" value={clientId} onChange={(e) => setClientId(e.target.value)} required data-testid="doc-form-client">
                        <option value="">Seleziona…</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">Nome documento {files.length > 1 ? "(ignorato in upload multiplo — verrà usato il nome originale di ogni file)" : "*"}</label>
                    <input
                        className="lt-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required={files.length <= 1}
                        minLength={files.length <= 1 ? 2 : 0}
                        maxLength={200}
                        disabled={files.length > 1}
                        data-testid="doc-form-name"
                    />
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">Tipo *</label>
                    <div className="lt-radio-row" data-testid="doc-form-type">
                        {TYPES.map((t) => (
                            <button key={t} type="button" className={"lt-radio-chip" + (type === t ? " is-active" : "")} onClick={() => setType(t)} data-testid={`doc-form-type-${t}`}>
                                {typeIcon(t)} {TYPE_LABEL[t]}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">Categoria (opzionale)</label>
                    <select className="lt-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="doc-form-category">
                        <option value="">— Nessuna categoria —</option>
                        {activeCategories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    {activeCategories.length === 0 && (
                        <div className="lt-hint" style={{ fontSize: 12, color: "#8A8F88", marginTop: 4 }}>
                            Nessuna categoria disponibile. Creane una da «Gestisci categorie».
                        </div>
                    )}
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">File * (uno o più)</label>
                    <div className="lt-file-picker" data-testid="doc-form-file-picker">
                        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} onChange={onFile} style={{ display: "none" }} data-testid="doc-form-file" />
                        <button type="button" className="lt-btn-secondary" onClick={onPick}>
                            <Upload size={14} /> Scegli file
                        </button>
                        <span className="lt-file-name">
                            {files.length === 0
                                ? "Nessun file selezionato"
                                : files.length === 1
                                    ? `${files[0].name} · ${fmtSize(files[0].size)}`
                                    : `${files.length} file selezionati (${fmtSize(files.reduce((s, f) => s + f.size, 0))} totali)`}
                        </span>
                    </div>
                    <div className="lt-hint" style={{ fontSize: 12, color: "#8A8F88", marginTop: 6 }} data-testid="doc-form-file-hint">
                        {ACCEPT_HINT}
                    </div>
                </div>
                <div className="lt-form-row">
                    <label className="lt-label">Note (opzionale)</label>
                    <textarea className="lt-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} data-testid="doc-form-notes" />
                </div>
                <div className="lt-toggle-row" data-testid="doc-form-vis-row">
                    <label className="lt-toggle">
                        <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="doc-form-vis" />
                        <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                        <span className="lt-toggle__label">Visibile al cliente</span>
                    </label>
                    <p className="lt-toggle__hint">Quando attivo, {files.length > 1 ? "i documenti appaiono" : "il documento appare"} nel portale del cliente.</p>
                </div>
                <div className="lt-form-actions">
                    <div style={{ flex: 1 }} />
                    <button type="button" className="lt-btn-secondary" onClick={onClose} data-testid="doc-form-cancel">Annulla</button>
                    <button type="submit" className="lt-btn-primary" disabled={saving} onClick={openConfirm} data-testid="doc-form-save">
                        {saving ? "Caricamento…" : "Carica"}
                    </button>
                </div>
            </form>

            {showConfirm && selectedClient && (
                <UploadConfirmationModal
                    client={selectedClient}
                    files={files}
                    clients={clients}
                    saving={saving}
                    onConfirm={doUploadAll}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </>
    );
}

function EditForm({ item, onSave, onClose, saving, categories }) {
    const [name, setName] = useState(item.name || "");
    const [type, setType] = useState(item.type || "Altro");
    const [visible, setVisible] = useState(Boolean(item.visible_to_client));
    const [notes, setNotes] = useState(item.notes || "");
    const [description, setDescription] = useState(item.description || "");
    const [categoryId, setCategoryId] = useState(item.category?.id || "");

    const submit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (saving) return;
        if (!name.trim()) { toast.error("Nome richiesto"); return; }
        onSave({
            name: name.trim(), type,
            visible_to_client: visible,
            notes: notes.trim() || null,
            description: description.trim() || null,
            category_id: categoryId || null,
        });
    };

    return (
        <form onSubmit={submit} method="post" action="#" noValidate className="lt-scad-form" data-testid="doc-edit-form">
            <div className="lt-form-row">
                <label className="lt-label">Nome</label>
                <input className="lt-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={200} data-testid="doc-edit-name" />
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Descrizione</label>
                <textarea className="lt-textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} placeholder="Descrizione opzionale del documento" data-testid="doc-edit-description" />
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Categoria</label>
                <select className="lt-input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="doc-edit-category">
                    <option value="">— Nessuna categoria —</option>
                    {(categories || []).filter(c => c.active !== false).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Tipo</label>
                <div className="lt-radio-row">
                    {TYPES.map((t) => (
                        <button key={t} type="button" className={"lt-radio-chip" + (type === t ? " is-active" : "")} onClick={() => setType(t)} data-testid={`doc-edit-type-${t}`}>
                            {typeIcon(t)} {TYPE_LABEL[t]}
                        </button>
                    ))}
                </div>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Note</label>
                <textarea className="lt-textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} data-testid="doc-edit-notes" />
            </div>
            <div className="lt-toggle-row">
                <label className="lt-toggle">
                    <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="doc-edit-vis" />
                    <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                    <span className="lt-toggle__label">Visibile al cliente</span>
                </label>
                <p className="lt-toggle__hint">Quando attivo, il documento appare nel portale del cliente.</p>
            </div>
            <div className="lt-meta-grid" data-testid="doc-edit-meta">
                <div><span className="lt-label">File originale</span><div>{item.original_filename}</div></div>
                <div><span className="lt-label">Dimensione</span><div>{fmtSize(item.size)}</div></div>
                <div><span className="lt-label">Caricato il</span><div>{fmtDateTime(item.created_at)}</div></div>
                <div><span className="lt-label">Da</span><div>{item.uploaded_by?.name || "—"}</div></div>
            </div>
            <div className="lt-form-actions">
                <div style={{ flex: 1 }} />
                <button type="button" className="lt-btn-secondary" onClick={onClose} data-testid="doc-edit-cancel">Annulla</button>
                <button type="submit" className="lt-btn-primary" disabled={saving} onClick={submit} data-testid="doc-edit-save">Salva</button>
            </div>
        </form>
    );
}

function ConfirmDelete({ name, onConfirm, onCancel }) {
    return (
        <div className="lt-confirm" data-testid="doc-confirm">
            <h3 className="lt-confirm__title">Eliminare il documento?</h3>
            <p className="lt-confirm__sub">«{name}» verrà rimosso definitivamente da R2 e dal database.</p>
            <div className="lt-form-actions">
                <div style={{ flex: 1 }} />
                <button className="lt-btn-secondary" onClick={onCancel} data-testid="doc-confirm-cancel">Annulla</button>
                <button className="lt-btn-danger" onClick={onConfirm} data-testid="doc-confirm-yes">Elimina</button>
            </div>
        </div>
    );
}

function KebabMenu({ item, onDownload, onDelete, onMarkVerified, canMarkVerified }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const pick = (fn) => (e) => { e.stopPropagation(); setOpen(false); fn(); };

    return (
        <div ref={wrapRef} className="lt-kebab-wrap" data-testid={`doc-actions-${item.id}`}>
            <button
                type="button"
                className="lt-icon-btn"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                aria-haspopup="menu"
                aria-expanded={open}
                title="Azioni"
                data-testid={`doc-kebab-${item.id}`}
            >
                <MoreVertical size={18} strokeWidth={2} />
            </button>
            {open && (
                <div className="lt-kebab-menu" role="menu" data-testid={`doc-kebab-menu-${item.id}`}>
                    <button type="button" role="menuitem" className="lt-kebab-item" onClick={pick(onDownload)} data-testid={`doc-action-download-${item.id}`}>
                        <Download size={14} strokeWidth={2} /> <span>Scarica</span>
                    </button>
                    {canMarkVerified && item.needs_review && (
                        <button type="button" role="menuitem" className="lt-kebab-item" onClick={pick(onMarkVerified)} data-testid={`doc-action-mark-verified-${item.id}`}>
                            <CheckCircle2 size={14} strokeWidth={2} /> <span>Segna come verificato</span>
                        </button>
                    )}
                    <button type="button" role="menuitem" className="lt-kebab-item lt-kebab-item--danger" onClick={pick(onDelete)} data-testid={`doc-action-delete-${item.id}`}>
                        <Trash2 size={14} strokeWidth={2} /> <span>Elimina</span>
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * RowActions — azioni direttamente visibili sulla riga documento.
 * Layout: [Eye] [Pencil] [Kebab⋮]. Il kebab tiene solo azioni secondarie
 * (Scarica + Elimina + Mark-verified se applicabile).
 */
function RowActions({ item, onPreview, onEdit, onDownload, onDelete, onMarkVerified, canMarkVerified }) {
    return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }} data-testid={`doc-row-actions-${item.id}`}>
            <button
                type="button"
                className="lt-icon-btn"
                onClick={(e) => { e.stopPropagation(); onPreview(); }}
                title="Visualizza"
                aria-label="Visualizza documento"
                data-testid={`doc-action-preview-${item.id}`}
            >
                <Eye size={18} strokeWidth={2} />
            </button>
            <button
                type="button"
                className="lt-icon-btn"
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                title="Modifica"
                aria-label="Modifica documento"
                data-testid={`doc-action-edit-${item.id}`}
            >
                <Pencil size={16} strokeWidth={2} />
            </button>
            <KebabMenu
                item={item}
                onDownload={onDownload}
                onDelete={onDelete}
                onMarkVerified={onMarkVerified}
                canMarkVerified={canMarkVerified}
            />
        </div>
    );
}

export default function Documenti() {
    const { counters, refresh: refreshCounters } = useCounters();
    const { user } = useAuth();
    const canMarkVerified = user?.role === "admin" || user?.role === "super_admin" || user?.is_super_admin === true;
    const dc = counters?.documenti || {};
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [clientFilter, setClientFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [visFilter, setVisFilter] = useState("all");
    const [clients, setClients] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [showCatsModal, setShowCatsModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [previewItem, setPreviewItem] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [blockedInfo, setBlockedInfo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { visible_to_client: visFilter, page: 1, page_size: 100 };
            if (q.trim()) params.q = q.trim();
            if (clientFilter) params.client_id = clientFilter;
            if (typeFilter) params.type = typeFilter;
            if (categoryFilter) params.category_id = categoryFilter;
            const { data } = await api.get("/internal/documenti", { params });
            setItems(data.items);
            setTotal(data.total);
        } catch {
            toast.error("Errore caricamento documenti");
        } finally { setLoading(false); }
    }, [q, clientFilter, typeFilter, categoryFilter, visFilter]);

    useEffect(() => {
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } })
            .then(({ data }) => setClients(data.items || data))
            .catch(() => {});
    }, []);

    const fetchCategories = useCallback(() => {
        api.get("/internal/document-categories", { params: { active: "all" } })
            .then(({ data }) => setCategories(data.items || []))
            .catch(() => {});
    }, []);
    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    useEffect(() => { fetchList(); }, [fetchList]);

    const onUploaded = () => {
        setShowUpload(false);
        fetchList();
        refreshCounters();
    };

    const saveEdit = async (payload) => {
        setSaving(true);
        try {
            await api.patch(`/internal/documenti/${editItem.id}`, payload);
            toast.success("Documento aggiornato");
            setEditItem(null);
            fetchList();
            refreshCounters();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally { setSaving(false); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/internal/documenti/${confirm.id}`);
            toast.success("Documento eliminato");
            setConfirm(null);
            setEditItem(null);
            fetchList();
            refreshCounters();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        }
    };

    const doMarkVerified = async (docId) => {
        try {
            await api.post(`/internal/documenti/${docId}/mark-verified`);
            toast.success("Documento segnato come verificato");
            fetchList();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        }
    };

    const counterCards = useMemo(() => [
        { key: "totali",   label: "Totali",              value: dc.totali ?? 0 },
        { key: "visibili", label: "Visibili al cliente", value: dc.visibili_al_cliente ?? 0 },
        { key: "interni",  label: "Solo interni",        value: dc.interni ?? 0 },
        { key: "contratti", label: "Contratti",          value: dc.per_tipo?.Contratto ?? 0 },
    ], [dc]);

    const activeCategories = useMemo(() => categories.filter(c => c.active !== false), [categories]);

    return (
        <div style={{ padding: "32px 40px" }} data-testid="doc-page">
            <div className="flex items-center" style={{ marginBottom: 20 }}>
                <h1 className="lt-h1" style={{ flex: 1 }}>Documenti</h1>
                <button className="lt-btn-primary" onClick={() => setShowUpload(true)} data-testid="doc-upload-btn">
                    <Plus size={14} /> Carica documento
                </button>
            </div>

            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }} data-testid="doc-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`doc-counter-${c.key}`}>
                        <div className="lt-counter-card__value">{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <input type="search" className="lt-input" placeholder="Cerca per nome o file…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} data-testid="doc-search" />
                <select className="lt-select" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="doc-filter-client">
                    <option value="">Tutti i clienti</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <div className="lt-segmented" data-testid="doc-filter-type">
                    <button type="button" className={"lt-segmented__btn" + (typeFilter === "" ? " is-active" : "")} onClick={() => setTypeFilter("")}>Tutti</button>
                    {TYPES.map((t) => (
                        <button key={t} type="button" className={"lt-segmented__btn" + (typeFilter === t ? " is-active" : "")} onClick={() => setTypeFilter(t)}>{TYPE_LABEL[t]}</button>
                    ))}
                </div>
                {activeCategories.length > 0 && (
                    <select className="lt-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="doc-filter-category">
                        <option value="">Tutte le categorie</option>
                        {activeCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
                <select className="lt-select" value={visFilter} onChange={(e) => setVisFilter(e.target.value)} data-testid="doc-filter-vis">
                    {VIS_LABELS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
                <div style={{ flex: 1 }} />
                <button type="button" className="lt-btn-secondary" onClick={() => setShowCatsModal(true)} data-testid="doc-manage-categories-btn">Gestisci categorie</button>
            </div>

            <div className="lt-card" style={{ padding: 0 }} data-testid="doc-table-wrap">
                <table className="lt-table" data-testid="doc-table">
                    <thead>
                        <tr>
                            <th style={{ width: 140 }}>Tipo</th>
                            <th>Nome</th>
                            <th>Cliente</th>
                            <th style={{ width: 100 }}>Dim.</th>
                            <th style={{ width: 140 }}>Visibilità</th>
                            <th style={{ width: 170 }} className="hidden lg:table-cell">Caricato il</th>
                            <th style={{ width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} style={{ padding: 24, color: "#8A8F88" }}>Caricamento…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: 24, color: "#8A8F88" }} data-testid="doc-empty">Nessun documento con i filtri correnti.</td></tr>
                        ) : (
                            items.map((it) => (
                                <tr key={it.id} data-testid={`doc-row-${it.id}`} onClick={() => setEditItem(it)} style={{ cursor: "pointer" }}>
                                    <td><TypeChip type={it.type} /></td>
                                    <td>
                                        <div style={{ fontWeight: 500, color: "#181B20", display: "inline-flex", alignItems: "center", gap: 6 }}>
                                            {it.name}
                                            {it.needs_review && (
                                                <span
                                                    data-testid={`doc-needs-review-badge-${it.id}`}
                                                    title="Documento da verificare (nessun cliente rilevato automaticamente)"
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", gap: 4,
                                                        padding: "2px 6px", borderRadius: 3,
                                                        background: "rgba(229,178,0,0.15)",
                                                        color: "#B27A00",
                                                        border: "1px solid rgba(229,178,0,0.45)",
                                                        fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase",
                                                        fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <AlertCircle size={11} strokeWidth={2.5} /> Da verificare
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ color: "#8A8F88", fontSize: 12, marginTop: 2 }}>
                                            {it.original_filename}
                                            {it.category?.name && <span data-testid={`doc-row-category-${it.id}`}> · <span style={{ color: "#5F6560" }}>{it.category.name}</span></span>}
                                        </div>
                                    </td>
                                    <td><span style={{ color: "#5F6560" }}>{it.client?.company_name || "—"}</span></td>
                                    <td style={{ color: "#5F6560", fontSize: 13 }}>{fmtSize(it.size)}</td>
                                    <td><VisibilityBadge visible={it.visible_to_client} /></td>
                                    <td className="hidden lg:table-cell" style={{ color: "#5F6560", fontSize: 13 }}>{fmtDateTime(it.created_at)}</td>
                                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                                        <RowActions
                                            item={it}
                                            onPreview={() => setPreviewItem(it)}
                                            onEdit={() => setEditItem(it)}
                                            onDownload={() => streamDownload(it.id, it.original_filename)}
                                            onDelete={() => setConfirm({ id: it.id, name: it.name })}
                                            onMarkVerified={() => doMarkVerified(it.id)}
                                            canMarkVerified={canMarkVerified}
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showUpload && (
                <div className="lt-modal-backdrop" onClick={() => setShowUpload(false)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="doc-upload-modal">
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">Carica documento</h2>
                            <button className="lt-icon-btn" onClick={() => setShowUpload(false)} aria-label="Chiudi"><X size={16} /></button>
                        </div>
                        <UploadForm
                            clients={clients}
                            categories={categories}
                            onClose={() => setShowUpload(false)}
                            onUploaded={onUploaded}
                            onBlocked={(info) => setBlockedInfo(info)}
                        />
                    </div>
                </div>
            )}

            {editItem && !confirm && (
                <div className="lt-modal-backdrop" onClick={() => setEditItem(null)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="doc-edit-modal">
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">Modifica documento</h2>
                            <button className="lt-icon-btn" onClick={() => setEditItem(null)} aria-label="Chiudi"><X size={16} /></button>
                        </div>
                        <EditForm
                            item={editItem}
                            categories={categories}
                            onSave={saveEdit}
                            onClose={() => setEditItem(null)}
                            saving={saving}
                        />
                    </div>
                </div>
            )}

            {previewItem && (
                <DocumentPreviewModal
                    item={previewItem}
                    downloadUrl={`${API}/internal/documenti/${previewItem.id}/download`}
                    onDownload={() => streamDownload(previewItem.id, previewItem.original_filename)}
                    onClose={() => setPreviewItem(null)}
                />
            )}

            {showCatsModal && (
                <DocumentCategoriesModal
                    categories={categories}
                    onClose={() => setShowCatsModal(false)}
                    onChange={fetchCategories}
                />
            )}

            {confirm && (
                <div className="lt-modal-backdrop" onClick={() => setConfirm(null)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <ConfirmDelete name={confirm.name} onConfirm={doDelete} onCancel={() => setConfirm(null)} />
                    </div>
                </div>
            )}

            {blockedInfo && (
                <UploadBlockedModal
                    detail={blockedInfo.detail}
                    filename={blockedInfo.filename}
                    file={blockedInfo.file}
                    uploadContext={blockedInfo.uploadContext}
                    onClose={() => setBlockedInfo(null)}
                    onOverrideSuccess={() => {
                        setBlockedInfo(null);
                        fetchList();
                        refreshCounters();
                    }}
                />
            )}
        </div>
    );
}
